import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputBase,
    Link,
    Skeleton,
    Stack,
    Typography,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MfaSetupInstructions from "components/Auth/MfaSetupInstructions";
import AcxConfirmationDialog from "components/UI/AcxConfirmationDialog";
import { AcxMessageBox as MessageBox } from "components/UI/AcxMessageBox";
import AcxButton from "components/UI/AcxButton";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxLogoLoader from "components/UI/Loaders/AcxLogoLoader";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ServiceError } from "services/BaseService";
import mfaService, {
    MfaMethod,
    MfaSetupCompleteResponse,
    MfaStatusResponse,
} from "services/MfaService";
import theme from "Theme/AppTheme";
import useCooldown from "utils/useCooldown";

type MfaStep =
    | "default"
    | "verify"
    | "resetVerify"
    | "setup"
    | "emailSetup"
    | "setupComplete";
type SetupMode = "enable" | "reset" | "email";
type PendingConfirmationAction =
    | "disable"
    | "regenerateRecoveryCodes"
    | "resetAuthenticator"
    | null;

type MfaSettingsProps = {
    onStatusChanged?: (status: MfaStatusResponse) => void;
    mode?: "profile" | "required";
    onContinue?: () => void | Promise<void>;
    onCancel?: () => void | Promise<void>;
};

const requiredNarrowFormWidth = 310;
const requiredWideFormWidth = 430;
const emailCodeResendCooldownSeconds = 60;

function MfaSettings({
    onStatusChanged,
    mode = "profile",
    onContinue,
    onCancel,
}: MfaSettingsProps = {}) {
    const [status, setStatus] = useState<MfaStatusResponse | null>(null);
    const [step, setStep] = useState<MfaStep>("default");
    const [setupMode, setSetupMode] = useState<SetupMode>("enable");
    const [manualEntryKey, setManualEntryKey] = useState("");
    const [authenticatorUri, setAuthenticatorUri] = useState("");
    const [emailDestination, setEmailDestination] = useState("");
    const [setupCode, setSetupCode] = useState("");
    const [selectedVerificationMethod, setSelectedVerificationMethod] =
        useState<MfaMethod>("authenticatorApp");
    const [challengeOrganizationId, setChallengeOrganizationId] = useState("");
    const [resetVerificationCode, setResetVerificationCode] = useState("");
    const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [emailCodeResent, setEmailCodeResent] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [pendingConfirmationAction, setPendingConfirmationAction] =
        useState<PendingConfirmationAction>(null);
    const emailCodeInputRefs = useRef<Array<HTMLInputElement | null>>([]);
    const {
        isCoolingDown: isEmailCodeCoolingDown,
        remainingSeconds: emailCodeCooldownSecondsRemaining,
        start: startEmailCodeCooldown,
    } = useCooldown(emailCodeResendCooldownSeconds);
    const isRequiredMode = mode === "required";
    const availableMethods = status?.availableMethods ?? [];
    const canUseAuthenticatorApp =
        availableMethods.includes("authenticatorApp");
    const canUseEmail = availableMethods.includes("email");
    const hasAuthenticatorMfa =
        status?.enabledMethods.includes("authenticatorApp") ?? false;
    const hasEmailMfa = status?.enabledMethods.includes("email") ?? false;
    const canVerifyAuthenticatorApp = hasAuthenticatorMfa;
    const canVerifyEmail = hasEmailMfa;
    const canSetupAuthenticatorApp =
        canUseAuthenticatorApp && !hasAuthenticatorMfa;
    const canSetupEmail = canUseEmail && !hasEmailMfa;
    const canSelectAuthenticatorApp =
        canVerifyAuthenticatorApp || canSetupAuthenticatorApp;
    const canSelectEmail = canVerifyEmail || canSetupEmail;

    const readErrorMessage = (error: unknown) => {
        if (error instanceof ServiceError) {
            return (
                error.errorMessage ?? "Something went wrong. Please try again."
            );
        }

        return "Something went wrong. Please try again.";
    };

    const loadStatus = useCallback(async () => {
        const response = await mfaService.getStatus();
        setStatus(response);
        onStatusChanged?.(response);
        return response;
    }, [onStatusChanged]);

    useEffect(() => {
        void (async () => {
            try {
                await loadStatus();
            } catch (error) {
                setErrorMessage(readErrorMessage(error));
            } finally {
                setIsLoading(false);
            }
        })();
    }, [loadStatus]);

    const resetSetupState = () => {
        setStep("default");
        setSetupCode("");
        setResetVerificationCode("");
        setManualEntryKey("");
        setAuthenticatorUri("");
        setEmailDestination("");
        setSelectedVerificationMethod("authenticatorApp");
        setChallengeOrganizationId("");
        setEmailCodeResent(false);
    };

    const applySetupComplete = (
        response: MfaSetupCompleteResponse,
        nextSuccessMessage: string,
    ) => {
        const completedMethod =
            setupMode === "email" ? "email" : "authenticatorApp";
        const updatedStatus = status
            ? {
                  ...status,
                  isEnabled: true,
                  requiresMfaSetup: false,
                  requiresMfaChallenge: false,
                  enabledMethods: Array.from(
                      new Set<MfaMethod>([
                          ...status.enabledMethods,
                          completedMethod,
                      ]),
                  ),
                  recoveryCodesRemaining: response.recoveryCodesRemaining,
                  isAuthenticatorAppEnabled:
                      setupMode === "email"
                          ? status.isAuthenticatorAppEnabled
                          : true,
                  isEmailEnabled:
                      setupMode === "email" ? true : status.isEmailEnabled,
                  hasAuthenticatorApp:
                      setupMode === "email" ? status.hasAuthenticatorApp : true,
              }
            : null;

        setRecoveryCodes(response.recoveryCodes || []);
        setStatus(updatedStatus);
        if (updatedStatus) {
            onStatusChanged?.(updatedStatus);
        }
        setStep("setupComplete");
        setSetupCode("");
        setErrorMessage("");
        setSuccessMessage(nextSuccessMessage);
    };

    const applySetupResponse = (
        manualEntryKeyValue: string,
        authenticatorUriValue: string,
        nextSetupMode: SetupMode,
    ) => {
        setSetupMode(nextSetupMode);
        setManualEntryKey(manualEntryKeyValue);
        setAuthenticatorUri(authenticatorUriValue);
        setRecoveryCodes([]);
        setSetupCode("");
        setStep("setup");
        setErrorMessage("");
    };

    const applyEmailSetupResponse = (maskedEmail: string) => {
        setSetupMode("email");
        setEmailDestination(maskedEmail);
        setRecoveryCodes([]);
        setSetupCode("");
        setStep("emailSetup");
        setErrorMessage("");
        setSuccessMessage("");
    };

    const showCurrentEmailSetup = () => {
        setSetupMode("email");
        setEmailDestination((current) => current || status?.maskedEmail || "");
        setRecoveryCodes([]);
        setSetupCode("");
        setStep("emailSetup");
        setErrorMessage("");
        setSuccessMessage("");
    };

    const handleBeginSetup = async () => {
        setIsSubmitting(true);
        setErrorMessage("");
        setSuccessMessage("");

        try {
            const response = await mfaService.beginManageSetup();
            applySetupResponse(
                response.manualEntryKey,
                response.authenticatorUri,
                "enable",
            );
        } catch (error) {
            setErrorMessage(readErrorMessage(error));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBeginEmailSetup = async () => {
        if (isEmailCodeCoolingDown) {
            showCurrentEmailSetup();
            return;
        }

        setIsSubmitting(true);
        setErrorMessage("");
        setSuccessMessage("");

        try {
            const response = await mfaService.beginManageEmailSetup();
            applyEmailSetupResponse(response.maskedEmail);
            startEmailCodeCooldown();
        } catch (error) {
            setErrorMessage(readErrorMessage(error));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBeginChallenge = async (method: MfaMethod) => {
        setIsSubmitting(true);
        setErrorMessage("");
        setSuccessMessage("");

        try {
            const response = await mfaService.beginChallenge();
            const challengeMethods = response.availableMethods?.length
                ? response.availableMethods
                : status?.enabledMethods ?? [];

            if (response.isSatisfied) {
                await loadStatus();
                await onContinue?.();
                return;
            }

            if (!challengeMethods.includes(method)) {
                setErrorMessage(
                    "That MFA method is not available for this organization.",
                );
                return;
            }

            setChallengeOrganizationId(response.organizationId);
            setSelectedVerificationMethod(method);
            setSetupCode("");
            setStep("verify");
            setEmailCodeResent(false);

            if (method === "email") {
                if (!isEmailCodeCoolingDown) {
                    const emailResponse = await mfaService.sendEmailCode(
                        response.organizationId,
                    );
                    setEmailDestination(emailResponse.maskedEmail);
                    startEmailCodeCooldown();
                } else {
                    setEmailDestination((current) => current || status?.maskedEmail || "");
                }
            }
        } catch (error) {
            setErrorMessage(readErrorMessage(error));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleVerifyChallenge = async () => {
        if (!setupCode.trim()) {
            setErrorMessage(
                selectedVerificationMethod === "email"
                    ? "Please enter the code sent to your email."
                    : "Please enter the 6-digit code from your authenticator app.",
            );
            return;
        }

        setIsSubmitting(true);
        setErrorMessage("");

        try {
            if (selectedVerificationMethod === "email") {
                await mfaService.verifyEmailCode(
                    setupCode,
                    challengeOrganizationId,
                );
            } else {
                await mfaService.verify(setupCode, challengeOrganizationId);
            }

            await loadStatus();
            await onContinue?.();
        } catch (error) {
            setErrorMessage(readErrorMessage(error));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleResendChallengeEmailCode = async () => {
        if (isEmailCodeCoolingDown || isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        setErrorMessage("");

        try {
            const response = await mfaService.sendEmailCode(
                challengeOrganizationId,
            );
            setEmailDestination(response.maskedEmail);
            setEmailCodeResent(true);
            setSetupCode("");
            startEmailCodeCooldown();
        } catch (error) {
            setErrorMessage(readErrorMessage(error));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBeginReset = async () => {
        if (!resetVerificationCode.trim()) {
            setErrorMessage(
                "Please enter the 6-digit code from your current authenticator app.",
            );
            return;
        }

        setIsSubmitting(true);
        setErrorMessage("");
        setSuccessMessage("");

        try {
            const response = await mfaService.beginResetAuthenticator(
                resetVerificationCode,
            );
            applySetupResponse(
                response.manualEntryKey,
                response.authenticatorUri,
                "reset",
            );
        } catch (error) {
            setErrorMessage(readErrorMessage(error));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCompleteSetup = async () => {
        if (!setupCode.trim()) {
            setErrorMessage(
                setupMode === "email"
                    ? "Please enter the code sent to your email."
                    : "Please enter the 6-digit code from your authenticator app.",
            );
            return;
        }

        setIsSubmitting(true);
        setErrorMessage("");

        try {
            const response =
                setupMode === "email"
                    ? await mfaService.completeManageEmailSetup(setupCode)
                    : setupMode === "enable"
                    ? await mfaService.completeManageSetup(setupCode)
                    : await mfaService.completeResetAuthenticator(setupCode);

            applySetupComplete(
                response,
                setupMode === "email"
                    ? "MFA is enabled for this account."
                    : setupMode === "enable"
                    ? "MFA is enabled for this account."
                    : "Your authenticator app has been reset. Save the new recovery codes and remove the old ones.",
            );
        } catch (error) {
            setErrorMessage(readErrorMessage(error));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRegenerateRecoveryCodes = async () => {
        setIsSubmitting(true);
        setErrorMessage("");
        setSuccessMessage("");

        try {
            const response = await mfaService.regenerateRecoveryCodes();
            setRecoveryCodes(response.recoveryCodes || []);
            setStatus((current) =>
                current == null
                    ? null
                    : {
                          ...current,
                          recoveryCodesRemaining:
                              response.recoveryCodesRemaining,
                      },
            );
            setStep("setupComplete");
            setSuccessMessage(
                "Recovery codes regenerated. Save the new codes and discard the old ones.",
            );
        } catch (error) {
            setErrorMessage(readErrorMessage(error));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDisable = async () => {
        setIsSubmitting(true);
        setErrorMessage("");
        setSuccessMessage("");

        try {
            const response = await mfaService.disable();
            setStatus(response);
            onStatusChanged?.(response);
            setRecoveryCodes([]);
            resetSetupState();
            setSuccessMessage("MFA has been disabled for this account.");
        } catch (error) {
            setErrorMessage(readErrorMessage(error));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCloseRecoveryCodes = async () => {
        if (isRequiredMode && onContinue) {
            await onContinue();
            return;
        }

        setRecoveryCodes([]);
        setStep("default");

        try {
            await loadStatus();
        } catch (error) {
            setErrorMessage(readErrorMessage(error));
        }
    };

    const closeConfirmationDialog = () => {
        if (!isSubmitting) {
            setPendingConfirmationAction(null);
        }
    };

    const handleConfirmedAction = async () => {
        switch (pendingConfirmationAction) {
            case "disable":
                await handleDisable();
                break;
            case "regenerateRecoveryCodes":
                await handleRegenerateRecoveryCodes();
                break;
            case "resetAuthenticator":
                setSetupMode("reset");
                setSetupCode("");
                setResetVerificationCode("");
                setRecoveryCodes([]);
                setErrorMessage("");
                setSuccessMessage("");
                setStep("resetVerify");
                break;
            default:
                return;
        }

        setPendingConfirmationAction(null);
    };

    const getConfirmationDialogContent = () => {
        switch (pendingConfirmationAction) {
            case "disable":
                return {
                    title: "Disable MFA?",
                    content:
                        "Disabling MFA removes the extra verification step from password login for this account. Are you sure you want to continue?",
                    confirmButtonText: "Disable MFA",
                };
            case "regenerateRecoveryCodes":
                return {
                    title: "Regenerate recovery codes?",
                    content:
                        "Generating new recovery codes will invalidate your existing unused recovery codes. Are you sure you want to continue?",
                    confirmButtonText: "Regenerate Codes",
                };
            case "resetAuthenticator":
                return {
                    title: "Reset authenticator app?",
                    content:
                        "Resetting your authenticator app will replace the current authenticator secret. Your existing authenticator app entry will stop working after you finish the reset.",
                    confirmButtonText: "Reset Authenticator",
                };
            default:
                return {
                    title: "",
                    content: "",
                    confirmButtonText: "Confirm",
                };
        }
    };

    const confirmationDialogContent = getConfirmationDialogContent();

    const actionButtonSx = {
        width: {
            xs: "100%",
            sm: "264.5px",
        },
        height: "36px",
    };

    const getMfaDescription = () => {
        if (canUseEmail && !canUseAuthenticatorApp) {
            return "Use email codes to protect this account.";
        }

        if (canUseAuthenticatorApp && !canUseEmail) {
            return "Use an authenticator app and recovery codes to protect this account.";
        }

        return "Use an authenticator app, email codes, and recovery codes to protect this account.";
    };

    const getEmailCodeActionLabel = (label: string) =>
        isEmailCodeCoolingDown
            ? `${label} in ${emailCodeCooldownSecondsRemaining}s`
            : label;
    const isEmailCodeActionDisabled = isEmailCodeCoolingDown || isSubmitting;

    const emailCodeActionSx = isEmailCodeActionDisabled
        ? {
              color: theme.palette.gray[500] ?? theme.palette.text.disabled,
              pointerEvents: "none" as const,
          }
        : undefined;

    const setEmailCodeDigit = (index: number, value: string) => {
        const nextDigits = value.replace(/\D/g, "");
        if (!nextDigits) {
            const nextCode = setupCode.split("");
            nextCode[index] = "";
            setSetupCode(nextCode.join(""));
            return;
        }

        if (nextDigits.length > 1) {
            const nextCode = nextDigits.slice(0, 6);
            setSetupCode(nextCode);
            emailCodeInputRefs.current[Math.min(nextCode.length, 5)]?.focus();
            return;
        }

        const nextCode = setupCode.padEnd(6, " ").split("");
        nextCode[index] = nextDigits;
        setSetupCode(nextCode.join("").replace(/\s/g, ""));
        emailCodeInputRefs.current[index + 1]?.focus();
    };

    const handleEmailCodeKeyDown = (
        index: number,
        event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        if (event.key === "Backspace" && !setupCode[index] && index > 0) {
            emailCodeInputRefs.current[index - 1]?.focus();
        }
    };

    const handleRequiredSetupKeyDown = (event: React.KeyboardEvent) => {
        if (event.key !== "Enter") {
            return;
        }

        event.preventDefault();
        if (step === "verify") {
            void handleVerifyChallenge();
            return;
        }

        void handleCompleteSetup();
    };

    const handleRequiredBack = () => {
        if (step === "default" && onCancel) {
            void onCancel();
            return;
        }

        resetSetupState();
    };

    const renderRequiredBackButton = () => (
        <Box
            component="button"
            type="button"
            onClick={handleRequiredBack}
            sx={{
                alignSelf: "flex-start",
                alignItems: "center",
                appearance: "none",
                background: "transparent",
                border: 0,
                color: theme.palette.gray[500],
                cursor: "pointer",
                display: "inline-flex",
                fontFamily: theme.typography.fontFamily,
                fontSize: 12,
                fontWeight: 600,
                gap: 0.5,
                lineHeight: "18px",
                m: 0,
                p: 0,
                "&:hover": {
                    color: theme.palette.gray[700],
                },
            }}
        >
            <KeyboardArrowLeftIcon sx={{ fontSize: 16 }} />
            Back
        </Box>
    );

    const renderRequiredTitle = (
        title: string,
        subtitle: string,
        options: { showBack?: boolean; showLogo?: boolean } = {},
    ) => (
        <Stack
            sx={{
                gap: options.showLogo ? 5 : 2.5,
                mb: options.showLogo ? 5 : 2.5,
                width: "100%",
            }}
        >
            {options.showLogo && (
                <AcxLogoLoader
                    height={55}
                    width={55}
                    isSpinning={isLoading || isSubmitting}
                />
            )}
            {options.showBack && renderRequiredBackButton()}
            <Stack sx={{ gap: 0.5, width: "100%" }}>
                <Typography
                    sx={{
                        color: theme.palette.gray[600],
                        fontSize: 24,
                        fontWeight: 700,
                        lineHeight: "40px",
                    }}
                >
                    {title}
                </Typography>
                <Typography
                    sx={{
                        color: theme.palette.gray[500],
                        fontSize: 16,
                        lineHeight: "24px",
                    }}
                >
                    {subtitle}
                </Typography>
            </Stack>
        </Stack>
    );

    const renderRequiredInlineError = () =>
        errorMessage ? (
            <MessageBox
                variant="error"
                sx={{ minHeight: 40, px: 1.5, py: 1 }}
                childrenSx={{ fontSize: "14px", lineHeight: "20px" }}
            >
                {errorMessage}
            </MessageBox>
        ) : null;

    const renderRequiredEmailCodeNotice = () => (
        <MessageBox
            key={emailCodeResent ? "resent-email-code" : "sent-email-code"}
            variant="info"
            icon={<CheckCircleRoundedIcon sx={{ fontSize: 20 }} />}
            sx={{ minHeight: 40, px: 1.5, py: 1 }}
            childrenSx={{
                fontSize: "13px",
                fontWeight: 600,
                lineHeight: "20px",
            }}
        >
            Check your inbox, your code should arrive shortly.
        </MessageBox>
    );

    const renderRequiredTryAnotherMethod = (onClick: () => void) => (
        <Typography
            sx={{
                color: theme.palette.gray[700],
                fontSize: 14,
                lineHeight: "20px",
                textAlign: "center",
            }}
        >
            Having trouble?{" "}
            <Link component="button" type="button" onClick={onClick}>
                Try another method
            </Link>
        </Typography>
    );

    const renderRequiredSetupMethodOption = ({
        label,
        icon,
        onClick,
    }: {
        label: string;
        icon: React.ReactNode;
        onClick: () => void;
    }) => (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            sx={{
                alignItems: "center",
                backgroundColor: theme.palette.white.main,
                border: `1px solid ${theme.palette.gray[200]}`,
                borderRadius: "4px",
                color: theme.palette.gray[700],
                cursor: "pointer",
                display: "flex",
                fontFamily: theme.typography.fontFamily,
                fontSize: 13,
                fontWeight: 600,
                gap: 1,
                height: 36,
                justifyContent: "space-between",
                lineHeight: "20px",
                p: "0 10px",
                textAlign: "left",
                width: "100%",
                "&:hover": {
                    borderColor: theme.palette.primary.main,
                },
            }}
        >
            <Stack
                sx={{
                    alignItems: "center",
                    flexDirection: "row",
                    gap: 1,
                    minWidth: 0,
                }}
            >
                {icon}
                <Box component="span">{label}</Box>
            </Stack>
            <ChevronRightIcon
                sx={{ color: theme.palette.gray[400], fontSize: 18 }}
            />
        </Box>
    );

    const renderRequiredEmailCodeInput = () => (
        <Stack
            sx={{
                flexDirection: "row",
                gap: 1,
                width: "100%",
            }}
        >
            {Array.from({ length: 6 }).map((_, index) => (
                <InputBase
                    key={index}
                    inputRef={(element) => {
                        emailCodeInputRefs.current[index] = element;
                    }}
                    value={setupCode[index] ?? ""}
                    onChange={(event) =>
                        setEmailCodeDigit(index, event.target.value)
                    }
                    onKeyDown={(event) => handleEmailCodeKeyDown(index, event)}
                    inputProps={{
                        "aria-label": `Email verification code digit ${
                            index + 1
                        }`,
                        autoComplete: "off",
                        inputMode: "numeric",
                        maxLength: 6,
                        name: `email-mfa-code-digit-${index + 1}`,
                    }}
                    sx={{
                        width: 34,
                        height: 40,
                        border: `1px solid ${theme.palette.gray[200]}`,
                        borderRadius: "4px",
                        color: theme.palette.gray[700],
                        fontSize: 16,
                        lineHeight: "24px",
                        "& input": {
                            padding: 0,
                            textAlign: "center",
                        },
                        "&.Mui-focused": {
                            borderColor: theme.palette.primary.main,
                        },
                    }}
                />
            ))}
        </Stack>
    );

    const renderRequiredPage = (
        title: string,
        subtitle: string,
        content: React.ReactNode,
        options: {
            width?: number;
            showBack?: boolean;
            showLogo?: boolean;
        } = {},
    ) => (
        <Stack
            sx={{
                width: options.width ?? requiredNarrowFormWidth,
                maxWidth: "100%",
            }}
        >
            {renderRequiredTitle(title, subtitle, {
                showBack: options.showBack,
                showLogo: options.showLogo,
            })}
            {content}
        </Stack>
    );

    const renderResetAuthenticatorDialog = () => (
        <Dialog
            open={step === "setup" && setupMode === "reset"}
            onClose={isSubmitting ? undefined : resetSetupState}
            PaperProps={{
                sx: {
                    width: "600px",
                    maxWidth: "calc(100vw - 48px)",
                    borderRadius: 2,
                    boxShadow: "0px 25px 50px rgba(0, 0, 0, 0.25)",
                },
            }}
        >
            <DialogTitle sx={{ p: 3, pb: 0 }}>
                <Stack sx={{ gap: 0.5 }}>
                    <Stack
                        sx={{
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                        }}
                    >
                        <Typography
                            sx={{
                                color: theme.palette.gray[600],
                                fontSize: 20,
                                fontWeight: 600,
                                lineHeight: "32px",
                            }}
                        >
                            Reset Authenticator App
                        </Typography>
                        <IconButton
                            aria-label="Close reset authenticator dialog"
                            onClick={resetSetupState}
                            disabled={isSubmitting}
                            size="small"
                            sx={{ color: theme.palette.gray[400] }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                    <Typography
                        sx={{
                            color: theme.palette.gray[700],
                            fontSize: 13,
                            lineHeight: "20px",
                        }}
                    >
                        Your current authenticator app will stop working as soon
                        as you finish this reset. Complete setup in your new
                        authenticator app before signing out.
                    </Typography>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
                <Stack sx={{ gap: 2.5 }}>
                    <MfaSetupInstructions
                        manualEntryKey={manualEntryKey}
                        authenticatorUri={authenticatorUri}
                    />
                    <AcxMainTextField
                        id="profile-mfa-reset-setup-code"
                        labelText="Enter Verification Code"
                        type="text"
                        autoComplete="off"
                        fullWidth
                        value={setupCode}
                        onChange={(event) => setSetupCode(event.target.value)}
                        placeholderText="Enter your one-time code..."
                        textContainerSx={{ maxHeight: "36px" }}
                    />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 0 }}>
                <AcxButton
                    color="primary"
                    onClick={() => void handleCompleteSetup()}
                    loading={isSubmitting}
                    sx={{ height: "36px" }}
                >
                    Reset
                </AcxButton>
            </DialogActions>
        </Dialog>
    );

    const renderRecoveryCodes = () => {
        if (recoveryCodes.length === 0) {
            return null;
        }

        return (
            <Stack sx={{ gap: 1 }}>
                <MessageBox variant="warning">
                    Save these recovery codes somewhere safe. Each code can be
                    used once if you lose access to your MFA method.
                </MessageBox>
                <Box
                    sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        padding: 1.5,
                        backgroundColor: theme.palette.gray[100],
                    }}
                >
                    {recoveryCodes.map((code) => (
                        <Typography
                            key={code}
                            sx={{
                                fontFamily: "monospace",
                                fontSize: "0.95rem",
                            }}
                        >
                            {code}
                        </Typography>
                    ))}
                </Box>
                <AcxButton
                    onClick={() => void handleCloseRecoveryCodes()}
                    color={isRequiredMode ? "primary" : undefined}
                    sx={{ width: "fit-content" }}
                >
                    {isRequiredMode ? "Continue" : "Done"}
                </AcxButton>
            </Stack>
        );
    };

    const renderSetupComplete = () => {
        if (setupMode === "email") {
            return (
                <Stack sx={{ gap: 1.5 }}>
                    <MessageBox variant="success">
                        Email MFA enabled. You'll receive a code to your inbox
                        each time you log in.
                    </MessageBox>
                    <AcxButton
                        onClick={() => void handleCloseRecoveryCodes()}
                        color={isRequiredMode ? "primary" : undefined}
                        sx={{ width: "fit-content" }}
                    >
                        {isRequiredMode ? "Continue" : "Done"}
                    </AcxButton>
                </Stack>
            );
        }

        return renderRecoveryCodes();
    };

    const renderRequiredMode = () => {
        if (isLoading) {
            return renderRequiredPage(
                "MFA required",
                "This organization requires multi-factor authentication for password users. Enable MFA to continue.",
                <Typography variant="body2">
                    Loading MFA settings...
                </Typography>,
                { showLogo: true },
            );
        }

        if (step === "verify") {
            const isEmailVerification = selectedVerificationMethod === "email";

            return renderRequiredPage(
                isEmailVerification
                    ? "Check your email"
                    : "Multi-factor Authentication",
                isEmailVerification
                    ? `We sent a 6 digit code to ${
                          emailDestination || "your email address"
                      }.`
                    : "Enter the verification code from your authenticator app to continue.",
                <Stack
                    component="form"
                    onKeyDown={handleRequiredSetupKeyDown}
                    sx={{ width: "100%", gap: 1.5 }}
                >
                    {isEmailVerification ? (
                        <>
                            {renderRequiredEmailCodeInput()}
                            {emailCodeResent && renderRequiredEmailCodeNotice()}
                        </>
                    ) : (
                        <AcxMainTextField
                            id="required-mfa-verification-code"
                            labelText="Enter Verification Code"
                            labelSx={{
                                color: theme.palette.gray[700],
                                fontSize: "13px",
                                lineHeight: "20px",
                                mb: 1,
                                "&.Mui-focused": {
                                    color: theme.palette.gray[700],
                                    fontWeight: 400,
                                },
                            }}
                            type="text"
                            autoComplete="off"
                            fullWidth
                            value={setupCode}
                            onChange={(event) =>
                                setSetupCode(event.target.value)
                            }
                            placeholderText="Enter your one-time code..."
                            textContainerSx={{ maxHeight: "36px" }}
                        />
                    )}
                    {renderRequiredInlineError()}
                    <AcxButton
                        fullWidth
                        color="primary"
                        onClick={() => void handleVerifyChallenge()}
                        loading={isSubmitting}
                        sx={{ height: "44px" }}
                    >
                        Verify
                    </AcxButton>
                    <Stack sx={{ alignItems: "center", gap: 1.5, mt: 0.5 }}>
                        {isEmailVerification && (
                            <Link
                                component="button"
                                type="button"
                                aria-disabled={isEmailCodeActionDisabled}
                                disabled={isEmailCodeActionDisabled}
                                onClick={() =>
                                    void handleResendChallengeEmailCode()
                                }
                                sx={emailCodeActionSx}
                            >
                                {getEmailCodeActionLabel("Resend code")}
                            </Link>
                        )}
                        {canVerifyAuthenticatorApp &&
                            canVerifyEmail &&
                            renderRequiredTryAnotherMethod(() =>
                                void handleBeginChallenge(
                                    isEmailVerification
                                        ? "authenticatorApp"
                                        : "email",
                                ),
                            )}
                    </Stack>
                </Stack>,
                {
                    width: isEmailVerification
                        ? requiredWideFormWidth
                        : requiredNarrowFormWidth,
                    showBack: true,
                },
            );
        }

        if (step === "setup" && setupMode !== "reset") {
            return renderRequiredPage(
                "Set up authenticator app",
                "Scan the QR code from your authenticator app. If you cannot scan it, enter the setup key manually instead.",
                <Stack
                    component="form"
                    onKeyDown={handleRequiredSetupKeyDown}
                    sx={{ width: "100%", gap: 1.5 }}
                >
                    <MfaSetupInstructions
                        manualEntryKey={manualEntryKey}
                        authenticatorUri={authenticatorUri}
                    />
                    <AcxMainTextField
                        id="required-mfa-setup-code"
                        labelText="Enter Verification Code"
                        labelSx={{
                            color: theme.palette.gray[700],
                            fontSize: "13px",
                            lineHeight: "20px",
                            mb: 1,
                            "&.Mui-focused": {
                                color: theme.palette.gray[700],
                                fontWeight: 400,
                            },
                        }}
                        type="text"
                        autoComplete="off"
                        fullWidth
                        value={setupCode}
                        onChange={(event) => setSetupCode(event.target.value)}
                        placeholderText="Enter your one-time code..."
                        textContainerSx={{ maxHeight: "36px" }}
                    />
                    {renderRequiredInlineError()}
                    <AcxButton
                        fullWidth
                        color="primary"
                        onClick={() => void handleCompleteSetup()}
                        loading={isSubmitting}
                        sx={{ height: "44px" }}
                    >
                        Verify
                    </AcxButton>
                    {canUseEmail && (
                        <Stack sx={{ alignItems: "center", mt: 0.5 }}>
                            {renderRequiredTryAnotherMethod(
                                () => void handleBeginEmailSetup(),
                            )}
                        </Stack>
                    )}
                </Stack>,
                { width: requiredWideFormWidth, showBack: true },
            );
        }

        if (step === "emailSetup") {
            return renderRequiredPage(
                "Check your email",
                `We sent a 6 digit code to ${
                    emailDestination || "your email address"
                }.`,
                <Stack
                    component="form"
                    onKeyDown={handleRequiredSetupKeyDown}
                    sx={{ width: "100%", gap: 1.5 }}
                >
                    {renderRequiredEmailCodeInput()}
                    {renderRequiredInlineError()}
                    <AcxButton
                        fullWidth
                        color="primary"
                        onClick={() => void handleCompleteSetup()}
                        loading={isSubmitting}
                        sx={{ height: "44px" }}
                    >
                        Verify
                    </AcxButton>
                    <Stack sx={{ alignItems: "center", gap: 1.5, mt: 0.5 }}>
                        <Link
                            component="button"
                            type="button"
                            aria-disabled={isEmailCodeActionDisabled}
                            disabled={isEmailCodeActionDisabled}
                            onClick={() => void handleBeginEmailSetup()}
                            sx={emailCodeActionSx}
                        >
                            {getEmailCodeActionLabel("Resend code")}
                        </Link>
                        {canUseAuthenticatorApp &&
                            renderRequiredTryAnotherMethod(
                                () => void handleBeginSetup(),
                            )}
                    </Stack>
                </Stack>,
                { width: requiredWideFormWidth, showBack: true },
            );
        }

        if (step === "setupComplete") {
            if (setupMode === "email") {
                return renderRequiredPage(
                    "Email MFA enabled.",
                    "You'll receive a code to your inbox each time you log in.",
                    <AcxButton
                        fullWidth
                        color="primary"
                        onClick={() => void handleCloseRecoveryCodes()}
                        sx={{ height: "44px" }}
                    >
                        Finish setup
                    </AcxButton>,
                    { showBack: true },
                );
            }

            return renderRequiredPage(
                "Recovery Codes",
                "Save these recovery codes somewhere safe. Each code can be used once if you lose access to your MFA method.",
                renderRecoveryCodes(),
                { width: requiredWideFormWidth, showBack: true },
            );
        }

        if (status?.requiresMfaChallenge) {
            return renderRequiredPage(
                "Secure your account",
                "This organization requires multi-factor authentication. Verify your identity to continue.",
                <Stack sx={{ gap: 1.5, width: "100%" }}>
                    {successMessage && (
                        <MessageBox variant="success">
                            {successMessage}
                        </MessageBox>
                    )}
                    {errorMessage && (
                        <MessageBox variant="error">{errorMessage}</MessageBox>
                    )}
                    {!canSelectAuthenticatorApp && !canSelectEmail && (
                        <MessageBox variant="warning">
                            No MFA methods are enabled for this account.
                        </MessageBox>
                    )}
                    {canSelectAuthenticatorApp &&
                        renderRequiredSetupMethodOption({
                            label: canSelectEmail
                                ? "Authenticator app (Recommended)"
                                : "Authenticator app",
                            icon: (
                                <LockOutlinedIcon
                                    sx={{
                                        color: theme.palette.gray[500],
                                        fontSize: 16,
                                    }}
                                />
                            ),
                            onClick: () =>
                                canVerifyAuthenticatorApp
                                    ? void handleBeginChallenge(
                                          "authenticatorApp",
                                      )
                                    : void handleBeginSetup(),
                        })}
                    {canSelectEmail &&
                        renderRequiredSetupMethodOption({
                            label: "Email",
                            icon: (
                                <EmailOutlinedIcon
                                    sx={{
                                        color: theme.palette.gray[500],
                                        fontSize: 16,
                                    }}
                                />
                            ),
                            onClick: () =>
                                canVerifyEmail
                                    ? void handleBeginChallenge("email")
                                    : void handleBeginEmailSetup(),
                        })}
                </Stack>,
                { width: requiredWideFormWidth, showBack: true },
            );
        }

        if (status && !status.isEnabled) {
            return renderRequiredPage(
                "Secure your account",
                "Multi-factor authentication (MFA) ensures your account remains secure by verifying your identity. First choose how you'll receive your six-digit verification code on the next screen.",
                <Stack sx={{ gap: 1.5, width: "100%" }}>
                    {successMessage && (
                        <MessageBox variant="success">
                            {successMessage}
                        </MessageBox>
                    )}
                    {errorMessage && (
                        <MessageBox variant="error">{errorMessage}</MessageBox>
                    )}
                    {!canUseAuthenticatorApp && !canUseEmail && (
                        <MessageBox variant="warning">
                            No MFA methods are enabled for this organization.
                        </MessageBox>
                    )}
                    {canUseAuthenticatorApp &&
                        renderRequiredSetupMethodOption({
                            label: "Authenticator app (Recommended)",
                            icon: (
                                <LockOutlinedIcon
                                    sx={{
                                        color: theme.palette.gray[500],
                                        fontSize: 16,
                                    }}
                                />
                            ),
                            onClick: () => void handleBeginSetup(),
                        })}
                    {canUseEmail &&
                        renderRequiredSetupMethodOption({
                            label: "Email",
                            icon: (
                                <EmailOutlinedIcon
                                    sx={{
                                        color: theme.palette.gray[500],
                                        fontSize: 16,
                                    }}
                                />
                            ),
                            onClick: () => void handleBeginEmailSetup(),
                        })}
                </Stack>,
                { width: requiredWideFormWidth, showBack: true },
            );
        }

        return renderRequiredPage(
            "MFA required",
            "This organization requires multi-factor authentication for password users. Enable MFA to continue.",
            <Stack sx={{ gap: 2, width: "100%" }}>
                <Stack sx={{ gap: 0.5 }}>
                    <Typography
                        sx={{
                            color: theme.palette.gray[600],
                            fontSize: 20,
                            fontWeight: 600,
                            lineHeight: "24px",
                        }}
                    >
                        Multi-Factor Authentication
                    </Typography>
                    <Typography
                        sx={{
                            color: theme.palette.gray[600],
                            fontSize: 13,
                            lineHeight: "16px",
                        }}
                    >
                        {getMfaDescription()}
                    </Typography>
                </Stack>

                {status && (
                    <MessageBox
                        sx={{
                            minHeight: "40px",
                            alignItems: "center",
                        }}
                        variant={
                            status.isEnabled
                                ? "success"
                                : status.organizationRequiresMfa
                                ? "warning"
                                : "info"
                        }
                    >
                        {status.isEnabled
                            ? hasAuthenticatorMfa
                                ? status.canDisable
                                    ? `MFA is enabled. ${status.recoveryCodesRemaining} recovery codes remain unused.`
                                    : `MFA is enabled and required by your organization. ${status.recoveryCodesRemaining} recovery codes remain unused.`
                                : hasEmailMfa
                                ? status.canDisable
                                    ? "MFA is enabled with email codes."
                                    : "MFA is enabled with email codes and required by your organization."
                                : "MFA is enabled."
                            : status.organizationRequiresMfa
                            ? "Your organization requires MFA for password logins. Enable it now to avoid being blocked at your next sign-in."
                            : "MFA is not enabled for this account."}
                    </MessageBox>
                )}

                {successMessage && (
                    <MessageBox variant="success">{successMessage}</MessageBox>
                )}

                {errorMessage && (
                    <MessageBox variant="error">{errorMessage}</MessageBox>
                )}

                {status && !status.isEnabled && (
                    <Stack sx={{ gap: 1.5, width: "100%" }}>
                        {!canUseAuthenticatorApp && !canUseEmail && (
                            <MessageBox variant="warning">
                                No MFA methods are enabled for this
                                organization.
                            </MessageBox>
                        )}
                        {canUseAuthenticatorApp && (
                            <AcxButton
                                fullWidth
                                color="primary"
                                onClick={() => void handleBeginSetup()}
                                loading={isSubmitting}
                                sx={{ height: "44px" }}
                            >
                                Use Authenticator App
                            </AcxButton>
                        )}
                        {canUseEmail && (
                            <AcxButton
                                fullWidth
                                color={
                                    canUseAuthenticatorApp
                                        ? undefined
                                        : "primary"
                                }
                                variant={
                                    canUseAuthenticatorApp
                                        ? "outlined"
                                        : undefined
                                }
                                onClick={() => void handleBeginEmailSetup()}
                                loading={isSubmitting}
                                sx={{ height: "44px" }}
                            >
                                Use Email Code
                            </AcxButton>
                        )}
                    </Stack>
                )}

                {status?.isEnabled && !status.requiresMfaChallenge && (
                    <AcxButton
                        fullWidth
                        color="primary"
                        onClick={() => void onContinue?.()}
                        sx={{ height: "44px" }}
                    >
                        Continue
                    </AcxButton>
                )}
            </Stack>,
            { showLogo: true },
        );
    };

    if (isRequiredMode) {
        return renderRequiredMode();
    }

    return (
        <Stack sx={{ gap: 2, maxWidth: isRequiredMode ? "100%" : "886px" }}>
            <Stack sx={{ gap: 0.5 }}>
                <Typography
                    sx={{
                        color: theme.palette.gray[600],
                        fontSize: 20,
                        fontWeight: 600,
                        lineHeight: "24px",
                    }}
                >
                    Multi-Factor Authentication
                </Typography>
                <Typography
                    sx={{
                        color: theme.palette.gray[600],
                        fontSize: 13,
                        lineHeight: "16px",
                    }}
                >
                    {getMfaDescription()}
                </Typography>
            </Stack>

            {status && (
                <MessageBox
                    sx={{
                        maxWidth: "632px",
                        minHeight: "40px",
                        alignItems: "center",
                    }}
                    variant={
                        status.isEnabled
                            ? "success"
                            : status.organizationRequiresMfa
                            ? "warning"
                            : "info"
                    }
                >
                    {status.isEnabled
                        ? hasAuthenticatorMfa
                            ? status.canDisable
                                ? `MFA is enabled. ${status.recoveryCodesRemaining} recovery codes remain unused.`
                                : `MFA is enabled and required by your organization. ${status.recoveryCodesRemaining} recovery codes remain unused.`
                            : hasEmailMfa
                            ? status.canDisable
                                ? "MFA is enabled with email codes."
                                : "MFA is enabled with email codes and required by your organization."
                            : "MFA is enabled."
                        : status.organizationRequiresMfa
                        ? "Your organization requires MFA for password logins. Enable it now to avoid being blocked at your next sign-in."
                        : "MFA is not enabled for this account."}
                </MessageBox>
            )}

            {successMessage && (
                <MessageBox variant="success">{successMessage}</MessageBox>
            )}

            {errorMessage && (
                <MessageBox variant="error">{errorMessage}</MessageBox>
            )}

            {isLoading && (
                <Stack sx={{ gap: 2 }}>
                    <Skeleton
                        variant="rounded"
                        sx={{ maxWidth: "632px", height: "40px" }}
                    />
                    <Stack sx={{ flexDirection: "row", gap: 2, flexWrap: "wrap" }}>
                        <Skeleton variant="rounded" sx={actionButtonSx} />
                        <Skeleton variant="rounded" sx={actionButtonSx} />
                    </Stack>
                </Stack>
            )}

            {!isLoading && step === "default" && status && !status.isEnabled && (
                <Stack
                    sx={{
                        flexDirection: "row",
                        gap: 2,
                        flexWrap: "wrap",
                    }}
                >
                    {!canUseAuthenticatorApp && !canUseEmail && (
                        <MessageBox variant="warning">
                            No MFA methods are enabled for this organization.
                        </MessageBox>
                    )}
                    {canUseAuthenticatorApp && (
                        <AcxButton
                            color="primary"
                            onClick={() => void handleBeginSetup()}
                            loading={isSubmitting}
                            sx={actionButtonSx}
                        >
                            Use Authenticator App
                        </AcxButton>
                    )}
                    {canUseEmail && (
                        <AcxButton
                            color={
                                canUseAuthenticatorApp ? undefined : "primary"
                            }
                            variant={
                                canUseAuthenticatorApp ? "outlined" : undefined
                            }
                            onClick={() => void handleBeginEmailSetup()}
                            loading={isSubmitting}
                            sx={actionButtonSx}
                        >
                            Use Email Code
                        </AcxButton>
                    )}
                </Stack>
            )}

            {!isLoading && step === "setup" && setupMode !== "reset" && (
                <Stack sx={{ gap: 1.5 }}>
                    <MfaSetupInstructions
                        manualEntryKey={manualEntryKey}
                        authenticatorUri={authenticatorUri}
                    />
                    <AcxMainTextField
                        id="profile-mfa-setup-code"
                        type="text"
                        autoComplete="off"
                        fullWidth
                        value={setupCode}
                        onChange={(event) => setSetupCode(event.target.value)}
                        placeholderText="Enter the 6-digit code from your authenticator app"
                        textContainerSx={{ maxHeight: "36px" }}
                    />
                    <Stack
                        sx={{
                            flexDirection: "row",
                            gap: 1,
                        }}
                    >
                        <AcxButton
                            color="primary"
                            onClick={() => void handleCompleteSetup()}
                            loading={isSubmitting}
                        >
                            {setupMode === "enable"
                                ? "Confirm Setup"
                                : "Finish Reset"}
                        </AcxButton>
                        {setupMode === "enable" && (
                            <AcxButton
                                variant="outlined"
                                onClick={resetSetupState}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </AcxButton>
                        )}
                    </Stack>
                </Stack>
            )}

            {!isLoading && step === "emailSetup" && (
                <Stack sx={{ gap: 1.5 }}>
                    <Typography variant="body2">
                        Enter the code sent to{" "}
                        {emailDestination || "your email address"}.
                    </Typography>
                    <AcxMainTextField
                        id="profile-mfa-email-setup-code"
                        type="text"
                        autoComplete="off"
                        fullWidth
                        value={setupCode}
                        onChange={(event) => setSetupCode(event.target.value)}
                        placeholderText="Enter your email code"
                        textContainerSx={{ maxHeight: "36px" }}
                    />
                    <Stack
                        sx={{
                            flexDirection: "row",
                            gap: 1,
                        }}
                    >
                        <AcxButton
                            color="primary"
                            onClick={() => void handleCompleteSetup()}
                            loading={isSubmitting}
                        >
                            Confirm Setup
                        </AcxButton>
                        <AcxButton
                            onClick={() => void handleBeginEmailSetup()}
                            disabled={isSubmitting || isEmailCodeCoolingDown}
                        >
                            {getEmailCodeActionLabel("Send New Code")}
                        </AcxButton>
                        <AcxButton
                            variant="outlined"
                            onClick={resetSetupState}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </AcxButton>
                    </Stack>
                </Stack>
            )}

            {!isLoading && step === "resetVerify" && (
                <Stack sx={{ gap: 1.5 }}>
                    <MessageBox variant="warning">
                        Enter a code from your current authenticator app before
                        replacing it.
                    </MessageBox>
                    <AcxMainTextField
                        id="profile-mfa-reset-current-code"
                        type="text"
                        autoComplete="off"
                        fullWidth
                        value={resetVerificationCode}
                        onChange={(event) =>
                            setResetVerificationCode(event.target.value)
                        }
                        placeholderText="Enter the 6-digit code from your current authenticator app"
                        textContainerSx={{ maxHeight: "36px" }}
                    />
                    <Stack
                        sx={{
                            flexDirection: "row",
                            gap: 1,
                        }}
                    >
                        <AcxButton
                            color="primary"
                            onClick={() => void handleBeginReset()}
                            loading={isSubmitting}
                        >
                            Continue Reset
                        </AcxButton>
                        <AcxButton
                            variant="outlined"
                            onClick={resetSetupState}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </AcxButton>
                    </Stack>
                </Stack>
            )}

            {!isLoading &&
                isRequiredMode &&
                status?.isEnabled &&
                step === "default" && (
                    <AcxButton
                        color="primary"
                        onClick={() => void onContinue?.()}
                        sx={{ width: "fit-content" }}
                    >
                        Continue
                    </AcxButton>
                )}

            {!isLoading &&
                !isRequiredMode &&
                status?.isEnabled &&
                step === "default" && (
                    <Stack
                        sx={{
                            flexDirection: "row",
                            gap: 2,
                            flexWrap: "wrap",
                        }}
                    >
                        {hasAuthenticatorMfa && (
                            <>
                                <AcxButton
                                    color="primary"
                                    onClick={() =>
                                        setPendingConfirmationAction(
                                            "regenerateRecoveryCodes",
                                        )
                                    }
                                    loading={isSubmitting}
                                    sx={actionButtonSx}
                                >
                                    Regenerate Recovery Codes
                                </AcxButton>
                                <AcxButton
                                    onClick={() =>
                                        setPendingConfirmationAction(
                                            "resetAuthenticator",
                                        )
                                    }
                                    loading={isSubmitting}
                                    disabled={!status.hasAuthenticatorApp}
                                    sx={actionButtonSx}
                                >
                                    Reset Authenticator App
                                </AcxButton>
                            </>
                        )}
                        {status.canDisable && (
                            <AcxButton
                                variant="outlined"
                                onClick={() =>
                                    setPendingConfirmationAction("disable")
                                }
                                loading={isSubmitting}
                                sx={actionButtonSx}
                            >
                                Disable MFA
                            </AcxButton>
                        )}
                    </Stack>
                )}

            {!isLoading && step === "setupComplete" && renderSetupComplete()}

            <AcxConfirmationDialog
                isOpen={pendingConfirmationAction !== null}
                title={confirmationDialogContent.title}
                content={confirmationDialogContent.content}
                confirmButtonText={confirmationDialogContent.confirmButtonText}
                onClose={closeConfirmationDialog}
                onConfirm={() => void handleConfirmedAction()}
                isLoading={isSubmitting}
                buttonsDisabled={isSubmitting}
            />
            {renderResetAuthenticatorDialog()}
        </Stack>
    );
}

export default MfaSettings;
